import axios from 'axios';
import { DateTime } from 'luxon';
import { action, computed, makeObservable } from 'mobx';

import { AlertsStore } from '@/configs/store/AlertsStore';
import { IS_V2_WRITE_ENABLED } from '@/configs/v2Write';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { LocalStore } from '@/models/LocalStore';
import { ValueModel } from '@/models/ValueModel';
import { trackerApi } from '@/tracker/api/trackerApi';
import { SEARCH_PAGE_SIZE } from '@/tracker/hooks/useServerSearch';
import { TrackerStore } from '@/tracker/model';
import {
  Board,
  Comment,
  CreatableTaskLinkRelationship,
  CreateTimelogRequest,
  Queue,
  Sprint,
  SprintRef,
  Task,
  TaskBasic,
  Timelog,
  UpdateTaskRequest,
} from '@/tracker/types';
import { showError } from '@/tracker/utils/showError';

/** Логов у задачи бывают сотни: история грузит их страницами */
const WORKLOGS_PAGE_SIZE = 50;

export class IssueCardStore extends LocalStore {
  private readonly _key: string;
  private readonly _trackerStore: TrackerStore;
  private readonly _alertsStore: AlertsStore;
  private readonly _issue = new ValueModel<Task | null>(null);
  private readonly _queue = new ValueModel<Queue | null>(null);
  /** Открытые спринты всех досок — грузятся один раз, при первом открытии выбора спринта */
  private _openSprints: Promise<Sprint[]> | null = null;
  private readonly _children = new ValueModel<TaskBasic[]>([]);
  private readonly _comments = new ValueModel<Comment[]>([]);
  private readonly _worklogs = new ValueModel<Timelog[]>([]);
  private readonly _worklogsPageToken = new ValueModel<string | null>(null);
  private readonly _worklogsStage = new LoadingStageModel();
  private readonly _isNotFound = new ValueModel(false);
  private readonly _loadingStage = new LoadingStageModel();
  private readonly _savingStage = new LoadingStageModel();

  constructor({
    key,
    trackerStore,
    alertsStore,
  }: {
    key: string;
    trackerStore: TrackerStore;
    alertsStore: AlertsStore;
  }) {
    super();
    this._key = key;
    this._trackerStore = trackerStore;
    this._alertsStore = alertsStore;

    makeObservable(this, {
      issue: computed,
      queueStatuses: computed,
      taskTypes: computed,
      children: computed,
      comments: computed,
      worklogs: computed,
      hasMoreWorklogs: computed,
      isWorklogsLoading: computed,
      isWorklogsLoaded: computed,
      isWorklogsError: computed,
      isLoading: computed,
      isNotFound: computed,
      isSaving: computed,
      load: action.bound,
      update: action.bound,
      changeStatus: action.bound,
      searchBoards: action.bound,
      addToBoard: action.bound,
      removeFromBoard: action.bound,
      searchSprints: action.bound,
      addToSprint: action.bound,
      removeFromSprint: action.bound,
      addChild: action.bound,
      addLink: action.bound,
      removeLink: action.bound,
      searchLinkCandidates: action.bound,
      addComment: action.bound,
      updateComment: action.bound,
      deleteComment: action.bound,
      searchTasks: action.bound,
      loadWorklogs: action.bound,
      loadMoreWorklogs: action.bound,
      addWorklog: action.bound,
      updateWorklog: action.bound,
      deleteWorklog: action.bound,
    });
  }

  get issue() {
    return this._issue.value;
  }

  /** Статусы очереди задачи — только их можно выставить, переходы свободные. null — не загружены */
  get queueStatuses() {
    return this._queue.value?.statuses ?? null;
  }

  /** Разрешённые типы задач очереди. Пустой список — очередь не загрузилась, выбор типа недоступен */
  get taskTypes() {
    return this._queue.value?.taskTypes ?? [];
  }

  get children() {
    return this._children.value;
  }

  get comments() {
    return this._comments.value;
  }

  /** Свежие сверху */
  get worklogs() {
    return [...this._worklogs.value].sort((a, b) => b.start.localeCompare(a.start));
  }

  get hasMoreWorklogs() {
    return this._worklogsPageToken.value !== null;
  }

  get isWorklogsLoading() {
    return this._worklogsStage.isLoading;
  }

  get isWorklogsLoaded() {
    return this._worklogsStage.isSuccess;
  }

  get isWorklogsError() {
    return this._worklogsStage.isError;
  }

  get isLoading() {
    return this._loadingStage.isLoading;
  }

  get isNotFound() {
    return this._isNotFound.value;
  }

  get isSaving() {
    return this._savingStage.isLoading;
  }

  /** Трекер пока бэкап Яндекс Трекера: карточку только смотрим (см. `IS_V2_WRITE_ENABLED`) */
  get isReadOnly() {
    return !IS_V2_WRITE_ENABLED;
  }

  async load() {
    try {
      this._loadingStage.loading();

      const [issue, children, comments] = await Promise.all([
        trackerApi.getTask(this._key),
        trackerApi.searchAllTasks({ filters: { parent: { eq: this._key } } }),
        trackerApi.listAllComments(this._key),
      ]);

      this._issue.change(issue);
      this._trackerStore.addRecent({ type: 'issue', id: issue.key, name: issue.summary });
      this.loadQueue(issue.queue.key);
      this._children.change(children);
      this._comments.change(comments);
      this._loadingStage.success();
    } catch (error) {
      this._loadingStage.error();

      // Опечатка в поиске, старая ссылка или чужая очередь (403 независимо от существования, AIP-211) —
      // не ошибка, карточка покажет «не найдена»
      if (axios.isAxiosError(error) && [403, 404].includes(error.response?.status ?? 0)) {
        this._isNotFound.change(true);

        return;
      }

      showError({ alertsStore: this._alertsStore, title: 'Не удалось загрузить задачу', error });
    }
  }

  /** Правка полей задачи. true — сохранено */
  async update(data: UpdateTaskRequest): Promise<boolean> {
    try {
      this._savingStage.loading();
      this.applyIssue(await trackerApi.updateTask({ key: this._key, data }));
      this._savingStage.success();

      return true;
    } catch (error) {
      this._savingStage.error();
      showError({ alertsStore: this._alertsStore, title: 'Не удалось сохранить задачу', error });

      return false;
    }
  }

  changeStatus(statusKey: string) {
    return this.update({ status: statusKey });
  }

  /** Куда добавить: подсказки сервера (мои доски первыми), кроме тех, где задача уже есть */
  async searchBoards(query: string): Promise<Board[]> {
    const current = new Set(this._issue.value?.boards.map((board) => board.id));

    try {
      const boards = await trackerApi.suggestBoards({
        query: query.trim() || undefined,
        pageSize: SEARCH_PAGE_SIZE,
      });

      return boards.filter((board) => !current.has(board.id));
    } catch {
      return [];
    }
  }

  addToBoard(boardId: string) {
    return this.changeAndReload({
      title: 'Не удалось добавить задачу на доску',
      run: () => trackerApi.addTasksToBoard({ boardId, keys: [this._key] }),
    });
  }

  /** Из спринтов этой доски задача уходит сама — так работает бэкенд */
  removeFromBoard(boardId: string) {
    return this.changeAndReload({
      title: 'Не удалось убрать задачу с доски',
      run: () => trackerApi.removeTasksFromBoard({ boardId, keys: [this._key] }),
    });
  }

  /**
   * Куда добавить: открытые спринты досок задачи, кроме текущих, по названию.
   * Задача ни на одной доске — спринты всех досок (на доску спринта она встанет сама)
   */
  async searchSprints(query: string): Promise<Sprint[]> {
    if (!this._openSprints) {
      this._openSprints = trackerApi
        .searchAllSprints({
          boardId: '-',
          request: { filters: { status: { in: ['active', 'draft'] } } },
        })
        .catch((error) => {
          // Следующее открытие выбора попробует снова
          this._openSprints = null;
          showError({
            alertsStore: this._alertsStore,
            title: 'Не удалось загрузить спринты',
            error,
          });

          return [];
        });
    }

    const sprints = await this._openSprints;
    const issue = this._issue.value;
    const boardIds = new Set(issue?.boards.map((board) => board.id));
    const currentIds = new Set(issue?.sprints.map((sprint) => sprint.id));
    const needle = query.trim().toLowerCase();

    return sprints.filter(
      (sprint) =>
        !currentIds.has(sprint.id) &&
        (!boardIds.size || boardIds.has(sprint.board.id)) &&
        sprint.name.toLowerCase().includes(needle),
    );
  }

  /** Из другого спринта той же доски задача уходит сама */
  addToSprint(sprint: SprintRef) {
    return this.changeAndReload({
      title: 'Не удалось добавить задачу в спринт',
      run: () =>
        trackerApi.addTasksToSprint({
          boardId: sprint.board.id,
          sprintId: sprint.id,
          keys: [this._key],
        }),
    });
  }

  removeFromSprint(sprint: SprintRef) {
    return this.changeAndReload({
      title: 'Не удалось убрать задачу из спринта',
      run: () =>
        trackerApi.removeTasksFromSprint({
          boardId: sprint.board.id,
          sprintId: sprint.id,
          keys: [this._key],
        }),
    });
  }

  /** Привязать существующую задачу подзадачей: у неё меняется родитель */
  async addChild(childKey: string) {
    try {
      this._savingStage.loading();

      const child = await trackerApi.updateTask({ key: childKey, data: { parent: this.issueKey } });

      this._children.change([...this._children.value, child]);
      this._trackerStore.notifyIssueUpdated(child);
      this._savingStage.success();
    } catch (error) {
      this._savingStage.error();
      showError({ alertsStore: this._alertsStore, title: 'Не удалось привязать подзадачу', error });
    }
  }

  /** Связи приходят в задаче (FULL) — после изменения перечитываем её: порядок и статусы задаёт сервер */
  addLink({ relationship, task }: { relationship: CreatableTaskLinkRelationship; task: string }) {
    return this.changeAndReload({
      title: 'Не удалось связать задачи',
      run: async () => {
        await trackerApi.createTaskLinks({
          key: this.issueKey,
          data: { links: [{ relationship, task }] },
        });
      },
    });
  }

  removeLink(id: string) {
    return this.changeAndReload({
      title: 'Не удалось удалить связь',
      run: () => trackerApi.deleteTaskLinks({ key: this.issueKey, ids: [id] }),
    });
  }

  /**
   * Кандидаты для связи — подсказки сервера без самой задачи и уже связанных: между двумя задачами
   * одна связь. Ошибка — пусто
   */
  async searchLinkCandidates(query: string): Promise<TaskBasic[]> {
    const excluded = new Set([
      this.issueKey,
      ...(this._issue.value?.links.map((link) => link.task.key) ?? []),
    ]);

    try {
      const tasks = await trackerApi.suggestTasks({
        query: query.trim() || undefined,
        pageSize: SEARCH_PAGE_SIZE,
      });

      return tasks.filter((task) => !excluded.has(task.key));
    } catch {
      return [];
    }
  }

  /** true — отправлен, форму можно очистить */
  async addComment(text: string): Promise<boolean> {
    try {
      this._savingStage.loading();

      const comment = await trackerApi.createComment({ key: this.issueKey, data: { text } });

      this._comments.change([...this._comments.value, comment]);
      this._savingStage.success();

      return true;
    } catch (error) {
      this._savingStage.error();
      showError({
        alertsStore: this._alertsStore,
        title: 'Не удалось отправить комментарий',
        error,
      });

      return false;
    }
  }

  /** etag — из списка: комментарий успели поменять — 409, и список подтянет свежую версию */
  async updateComment({ id, text }: { id: string; text: string }): Promise<boolean> {
    try {
      this._savingStage.loading();

      const etag = this._comments.value.find((comment) => comment.id === id)?.etag;
      const updated = await trackerApi.updateComment({
        key: this.issueKey,
        id,
        data: { text, etag },
      });

      this._comments.change(this._comments.value.map((c) => (c.id === id ? updated : c)));
      this._savingStage.success();

      return true;
    } catch (error) {
      this._savingStage.error();
      showError({
        alertsStore: this._alertsStore,
        title: 'Не удалось изменить комментарий',
        error,
      });

      if (axios.isAxiosError(error) && error.response?.status === 409) {
        await this.refreshComments();
      }

      return false;
    }
  }

  async deleteComment(id: string): Promise<boolean> {
    try {
      this._savingStage.loading();
      await trackerApi.deleteComment({ key: this.issueKey, id });
      this._comments.change(this._comments.value.filter((comment) => comment.id !== id));
      this._savingStage.success();

      return true;
    } catch (error) {
      this._savingStage.error();
      showError({ alertsStore: this._alertsStore, title: 'Не удалось удалить комментарий', error });

      return false;
    }
  }

  /**
   * Кандидаты в родители и подзадачи — подсказки сервера, без самой задачи и её подзадач.
   * Ошибка — пустой список
   */
  async searchTasks(query: string): Promise<TaskBasic[]> {
    const excluded = new Set([this.issueKey, ...this._children.value.map((child) => child.key)]);

    try {
      const tasks = await trackerApi.suggestTasks({
        query: query.trim() || undefined,
        pageSize: SEARCH_PAGE_SIZE,
      });

      return tasks.filter((task) => !excluded.has(task.key));
    } catch {
      return [];
    }
  }

  /** Первая страница истории логов — при каждом её открытии, чтобы видеть чужие свежие записи */
  loadWorklogs() {
    this._worklogs.change([]);
    this._worklogsPageToken.change(null);

    return this.fetchWorklogs(undefined);
  }

  loadMoreWorklogs() {
    const pageToken = this._worklogsPageToken.value;

    if (pageToken === null || this._worklogsStage.isLoading) return;

    return this.fetchWorklogs(pageToken);
  }

  /**
   * Запись времени и, если передан, новый остаток оценки — как форма «Учёт времени» в Яндекс Трекере.
   * true — запись создана (форму можно закрыть), даже если остаток обновить не удалось
   */
  async addWorklog({
    worklog,
    estimation,
  }: {
    worklog: Omit<CreateTimelogRequest, 'task'>;
    /** undefined — остаток не меняем */
    estimation?: string;
  }): Promise<boolean> {
    this._savingStage.loading();

    try {
      const created = await trackerApi.createTimelog({ ...worklog, task: this._key });

      this._worklogs.change([...this._worklogs.value, created]);
    } catch (error) {
      this._savingStage.error();
      showError({ alertsStore: this._alertsStore, title: 'Не удалось записать время', error });

      return false;
    }

    if (estimation !== undefined && estimation !== this._issue.value?.estimation) {
      try {
        this.applyIssue(await trackerApi.updateTask({ key: this._key, data: { estimation } }));
      } catch (error) {
        showError({
          alertsStore: this._alertsStore,
          title: 'Время записано, но остаток оценки не обновился',
          error,
        });
      }
    } else {
      await this.refreshIssue();
    }

    this._savingStage.success();

    return true;
  }

  /**
   * etag — из списка: если запись успели поменять, бэк ответит 409, а список подтянет свежую версию,
   * и повторное сохранение осознанно перезапишет чужую правку. true — сохранено
   */
  async updateWorklog({
    id,
    data,
  }: {
    id: string;
    data: Omit<CreateTimelogRequest, 'task'>;
  }): Promise<boolean> {
    try {
      this._savingStage.loading();

      const etag = this._worklogs.value.find((w) => w.id === id)?.etag;
      const updated = await trackerApi.updateTimelog({ id, data: { ...data, etag } });

      // Начало могли перенести — держим порядок «свежие сверху»
      this._worklogs.change(
        this._worklogs.value
          .map((w) => (w.id === updated.id ? updated : w))
          .sort(
            (a, b) => DateTime.fromISO(b.start).toMillis() - DateTime.fromISO(a.start).toMillis(),
          ),
      );
      await this.refreshIssue();
      this._savingStage.success();

      return true;
    } catch (error) {
      this._savingStage.error();
      showError({ alertsStore: this._alertsStore, title: 'Не удалось изменить запись', error });

      if (axios.isAxiosError(error) && error.response?.status === 409) {
        await this.refreshWorklog(id);
      }

      return false;
    }
  }

  private async refreshWorklog(id: string) {
    try {
      const fresh = await trackerApi.getTimelog(id);

      this._worklogs.change(this._worklogs.value.map((w) => (w.id === id ? fresh : w)));
    } catch {
      // Не вышло — свежую версию подтянет переоткрытие истории
    }
  }

  async deleteWorklog(id: string) {
    try {
      this._savingStage.loading();
      await trackerApi.deleteTimelog(id);
      this._worklogs.change(this._worklogs.value.filter((w) => w.id !== id));
      await this.refreshIssue();
      this._savingStage.success();
    } catch (error) {
      this._savingStage.error();
      showError({ alertsStore: this._alertsStore, title: 'Не удалось удалить запись', error });
    }
  }

  private async fetchWorklogs(pageToken: string | undefined) {
    try {
      this._worklogsStage.loading();

      const page = await trackerApi.searchTimelogs({
        filters: { task: { eq: this._key } },
        orderBy: [{ field: 'start', direction: 'desc' }],
        pageSize: WORKLOGS_PAGE_SIZE,
        pageToken,
      });
      // Записанный после открытия истории лог может прийти и со следующей страницей
      const loadedIds = new Set(this._worklogs.value.map((worklog) => worklog.id));

      this._worklogs.change([
        ...this._worklogs.value,
        ...page.items.filter((worklog) => !loadedIds.has(worklog.id)),
      ]);
      this._worklogsPageToken.change(page.nextPageToken);
      this._worklogsStage.success();
    } catch (error) {
      this._worklogsStage.error();
      showError({
        alertsStore: this._alertsStore,
        title: 'Не удалось загрузить историю записей',
        error,
      });
    }
  }

  /** Без очереди карточка работает, только выбор статуса и типа недоступен */
  private async loadQueue(queueKey: string) {
    try {
      this._queue.change(await trackerApi.getQueue(queueKey));
    } catch (error) {
      showError({
        alertsStore: this._alertsStore,
        title: 'Не удалось загрузить очередь задачи',
        error,
      });
    }
  }

  /** Затрачено (`spent`) считает сервер — после изменения логов перечитываем задачу для списков и доски */
  /** Доски, спринты и связи задачи меняются отдельными запросами — после них перечитываем задачу целиком */
  private async changeAndReload({ title, run }: { title: string; run: () => Promise<void> }) {
    try {
      this._savingStage.loading();
      await run();
      this.applyIssue(await trackerApi.getTask(this._key));
      this._savingStage.success();
    } catch (error) {
      this._savingStage.error();
      showError({ alertsStore: this._alertsStore, title, error });
    }
  }

  private async refreshComments() {
    try {
      this._comments.change(await trackerApi.listAllComments(this.issueKey));
    } catch {
      // Не вышло — свежий список подтянет переоткрытие карточки
    }
  }

  private async refreshIssue() {
    try {
      this.applyIssue(await trackerApi.getTask(this._key));
    } catch {
      // Не критично: лог уже записан, списки обновятся при следующей загрузке
    }
  }

  private applyIssue(issue: Task) {
    this._issue.change(issue);
    this._trackerStore.notifyIssueUpdated(issue);
  }

  /** Ключ из URL может быть старым ключом или uuid — для связей берём текущий */
  private get issueKey() {
    return this._issue.value?.key ?? this._key;
  }
}
